// @ts-ignore : let rollup handle CSS injection
import "../MyDataHelps.css";

import * as Model from "./types";
import { StringMap } from "./types";

export class MyDataHelps {

	baseUrl: string = 'https://mydatahelps.org/';
	language: string;
	token: Model.Token;
	tokenExpires: number;

	// Surveys

	startSurvey(surveyName: string): void {
		if (this.isStandaloneMode()) {
			this.startDelegatedSurvey(`${this.baseUrl}/survey?surveyName=${encodeURIComponent(surveyName)}`);
		}
		else if (window.webkit.messageHandlers.StartParticipantSurvey) {
			let messageID = this.nextMessageID();
			window.webkit.messageHandlers.StartParticipantSurvey.postMessage({
				messageID: messageID,
				surveyName: surveyName
			});
		}
	}

	completeStep(answer: any): void {
		if (answer === undefined || answer === null) answer = '';
		if (typeof answer !== 'string') {
			answer = JSON.stringify(answer);
		}
		if (window.webkit.messageHandlers.ResearchKit) {
			window.webkit.messageHandlers.ResearchKit.postMessage(answer);
		}
		else if (window.webkit.messageHandlers.CompleteStep) {
			let messageID = this.nextMessageID();
			window.webkit.messageHandlers.CompleteStep.postMessage({
				messageID: messageID,
				answer: answer
			});
		}
	}

	querySurveyAnswers(queryParameters: Model.SurveyAnswersQuery): Promise<Model.SurveyAnswersPage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("surveyName")) {
			queryParameters.surveyName = MyDataHelps.reduceArrayAndEscape(queryParameters.surveyName);
		}
		if (queryParameters.hasOwnProperty("stepIdentifier")) {
			queryParameters.stepIdentifier = MyDataHelps.reduceArrayAndEscape(queryParameters.stepIdentifier);
		}
		if (queryParameters.hasOwnProperty("resultIdentifier")) {
			queryParameters.resultIdentifier = MyDataHelps.reduceArrayAndEscape(queryParameters.resultIdentifier);
		}
		if (queryParameters.hasOwnProperty("answer")) {
			queryParameters.answer = MyDataHelps.reduceArrayAndEscape(queryParameters.answer);
		}
		if (queryParameters.hasOwnProperty("before")) {
			queryParameters.before = MyDataHelps.convertDateToIsoString(queryParameters.before);
		}
		if (queryParameters.hasOwnProperty("after")) {
			queryParameters.after = MyDataHelps.convertDateToIsoString(queryParameters.after);
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'surveyanswers?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	querySurveyResults(queryParameters: Model.SurveyResultsQuery): Promise<Model.SurveyResultsPage> {
		queryParameters = { ...queryParameters }

		if (queryParameters.hasOwnProperty("surveyName")) {
			queryParameters.surveyName = MyDataHelps.reduceArrayAndEscape(queryParameters.surveyName);
		}
		if (queryParameters.hasOwnProperty("event")) {
			queryParameters.event = MyDataHelps.reduceArrayAndEscape(queryParameters.event);
		}
		if (queryParameters.hasOwnProperty("before")) {
			queryParameters.before = MyDataHelps.convertDateToIsoString(queryParameters.before);
		}
		if (queryParameters.hasOwnProperty("after")) {
			queryParameters.after = MyDataHelps.convertDateToIsoString(queryParameters.after);
		}
		if (queryParameters.hasOwnProperty("insertedBefore")) {
			queryParameters.insertedBefore = MyDataHelps.convertDateToIsoString(queryParameters.insertedBefore);
		}
		if (queryParameters.hasOwnProperty("insertedAfter")) {
			queryParameters.insertedAfter = MyDataHelps.convertDateToIsoString(queryParameters.insertedAfter);
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'surveyresults?' + queryString;

		return this.connect()
			.then(() => this.makeRequest(endpoint, 'GET', null))
			.then(MyDataHelps.validateResponse)
			.then(response => response.json());
	}

	deleteSurveyResult(resultID: string): Promise<void> {
		const endpoint = 'surveyresults/' + encodeURIComponent(resultID);
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'DELETE', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function () {
				return;
			});
	}

	querySurveyTasks(queryParameters: Model.SurveyTaskQueryParameters): Promise<Model.SurveyTasksPage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("status")) {
			queryParameters.status = MyDataHelps.reduceArrayAndEscape(queryParameters.status) as Model.SurveyTaskStatus;
		}
		if (queryParameters.hasOwnProperty("surveyName")) {
			queryParameters.surveyName = MyDataHelps.reduceArrayAndEscape(queryParameters.surveyName);
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'surveytasks?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	queryInboxItems(queryParameters: Model.InboxItemQueryParameters): Promise<Model.InboxItemsPage> {
		const assignDefined = (target: any, source: any): any => {
			for (const key of Object.keys(source)) {
				const value = source[key];
				if (value !== undefined) {
					target[key] = value;
				}
			}
			return target;
		};

		queryParameters = assignDefined({}, queryParameters);

		if (queryParameters.hasOwnProperty("type")) {
			queryParameters.type = MyDataHelps.reduceArrayAndEscape(queryParameters.type) as Model.InboxItemType;
		}

		if (queryParameters.hasOwnProperty("status")) {
			queryParameters.status = MyDataHelps.reduceArrayAndEscape(queryParameters.status) as Model.InboxItemStatus;
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'inbox?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	getInboxItem(id: Model.Guid): Promise<Model.InboxItem> {
		const endpoint = `inbox/${id}`;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	updateInboxItem(id: Model.Guid, update: Model.InboxItemUpdate): Promise<Model.InboxItemUpdateResult> {
		const endpoint = `inbox/${id}/${update}`;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'PUT', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	startEmbeddedSurvey(linkIdentifier: string, surveyName: string, language: string): Promise<any> {
		if (!language) {
			language = this.getCurrentLanguage();
		}
		let surveyUrl = this.baseUrl + "mydatahelps/" + linkIdentifier + '/surveylink/' + surveyName + "?lang=" + language;
		return this.startEmbeddedSurveyInternal(surveyUrl);
	}

	// Navigation

	openExternalUrl(url: string): void {
		if (window.webkit.messageHandlers.OpenExternalLink) {
			window.webkit.messageHandlers.OpenExternalLink.postMessage(url);
		}
	}

	openEmbeddedUrl(url: string): void {
		if (window.webkit.messageHandlers.OpenEmbeddedLink) {
			window.webkit.messageHandlers.OpenEmbeddedLink.postMessage(url);
		}
	}

	showTab(tabKey: string): void {
		if (window.webkit.messageHandlers.ShowTab) {
			window.webkit.messageHandlers.ShowTab.postMessage(tabKey);
		}
	}

	openApplication(url: string, options?: Model.OpenApplicationOptions): void {
		if (window.webkit.messageHandlers.OpenExternalApplication) {
			let messageID = this.nextMessageID();
			window.webkit.messageHandlers.OpenExternalApplication.postMessage({
				messageID: messageID,
				url: url,
				modal: !!(options && options.modal)
			});
		}
	}

	dismiss(): void {
		if (window.webkit.messageHandlers.Dismiss) {
			window.webkit.messageHandlers.Dismiss.postMessage({});
		}
	}

	back(): void {
		if (window.webkit.messageHandlers.PopNavigation) {
			window.webkit.messageHandlers.PopNavigation.postMessage({});
		}
	}

	showDashboard(dashboardKey: string, options?: Model.ShowDashboardOptions): void {
		if (window.webkit.messageHandlers.ShowParticipantDashboard) {
			window.webkit.messageHandlers.ShowParticipantDashboard.postMessage({
				dashboardKey: dashboardKey,
				modal: !!(options && options.modal),
				title: options && options.title ? options.title : undefined
			});
		}
	}

	showWebVisualization(visualizationKey: string, parameters: object, options: Model.ShowWebVisualizationOptions): void {
		if (window.webkit.messageHandlers.ShowParticipantWebVisualization) {
			window.webkit.messageHandlers.ShowParticipantWebVisualization.postMessage({
				visualizationKey: visualizationKey,
				parameters: parameters,
				modal: !!(options && options.modal),
				title: options && options.title ? options.title : undefined
			});
		}
	}

	showWebVisualizationPdf(visualizationKey: string, parameters: object, options: Model.ShowWebVisualizationPdfOptions): void {
		if (window.webkit.messageHandlers.ShowParticipantWebVisualizationPDF) {
			window.webkit.messageHandlers.ShowParticipantWebVisualizationPDF.postMessage({
				visualizationKey: visualizationKey,
				parameters: parameters,
				landscape: !!(options && options.landscape),
				htmlViewerZoom: options && options.htmlViewerZoom
			});
		}
	}

	showProject(projectCode: string): void {
		if (window.webkit.messageHandlers.ShowProject) {
			window.webkit.messageHandlers.ShowProject.postMessage({ code: projectCode });
		}
	}

	joinProject(projectCode: string): void {
		if (window.webkit.messageHandlers.JoinProject) {
			window.webkit.messageHandlers.JoinProject.postMessage({ code: projectCode });
		}
	}

	//Events

	on(eventName: Model.EventName, callback: (EventData) => void): void {

		if (!this.supportedEvents.includes(eventName)) {
			throw new Error(eventName + " is not a supported event type.");
		}

		if (!this.registeredEventHandlers[eventName]) {
			this.registeredEventHandlers[eventName] = [];
		}

		this.registeredEventHandlers[eventName].push(callback);
	}

	off(eventName: Model.EventName, callback: (EventData) => void): void {

		if (!this.supportedEvents.includes(eventName)) {
			throw new Error(eventName + " is not a supported event type.");
		}

		if (!this.registeredEventHandlers[eventName]) return;

		let eventHandlerIndex = this.registeredEventHandlers[eventName].indexOf(callback);

		if (eventHandlerIndex !== -1) {
			this.registeredEventHandlers[eventName].splice(eventHandlerIndex, 1);
		}
	}

	triggerEvent(event: Model.EventData): void {
		let eventName = event.type;
		if (this.supportedEvents.includes(eventName) && this.registeredEventHandlers[eventName]) {
			this.registeredEventHandlers[eventName].forEach(function (handler) {
				handler(event);
			});
		}
	}

	setActionResult(data: Model.EventData): void {
		if (Object.prototype.hasOwnProperty.call(this.messageHandlers, data.messageID) &&
			typeof this.messageHandlers[data.messageID] === 'function') {
			this.messageHandlers[data.messageID](data);
		} else {
			console.error(`Invalid messageID: ${data.messageID}`);
		}
	}

	// Participant Info

	getParticipantInfo(): Promise<Model.ParticipantInfo> {
		const mdh = this;
		const endpoint = 'participant';

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	persistParticipantInfo(demographics: Partial<Model.ParticipantDemographics> | undefined, customFields: StringMap): Promise<Model.ParticipantInfo> {
		const endpoint = 'participant';
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'PUT', { demographics, customFields });
			})
			.then(async function (response) {
				if (!response.ok) {
					var errorJson = await response.json();
					throw errorJson.message;
				}
				return response.json();
			})
			.catch(function (error) {
				console.log("Unable to persist participant: " + error);
			});
	}

	getProjectInfo(): Promise<Model.ProjectInfo> {
		const mdh = this;
		const endpoint = 'project';

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	// Device Data

	getDeviceDataPoint(id: Model.Guid): Promise<Model.DeviceDataPoint> {
		const endpoint = 'devicedata/' + id;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	deleteDeviceDataPoint(id: Model.Guid): Promise<void> {
		const endpoint = 'devicedata/' + id;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'DELETE', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function () {
				return;
			});
	}

	queryDeviceData(queryParameters: Model.DeviceDataPointQuery): Promise<Model.DeviceDataPointsPage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("type")) {
			queryParameters.type = MyDataHelps.reduceArrayAndEscape(queryParameters.type);
		}
		if (queryParameters.hasOwnProperty("observedBefore")) {
			queryParameters.observedBefore = MyDataHelps.convertDateToIsoString(queryParameters.observedBefore);
		}
		if (queryParameters.hasOwnProperty("observedAfter")) {
			queryParameters.observedAfter = MyDataHelps.convertDateToIsoString(queryParameters.observedAfter);
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'devicedata?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	persistDeviceData(deviceDataPoints: Model.PersistableDeviceDataPoint[]): Promise<void> {
		const endpoint = 'devicedata';
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'POST', deviceDataPoints);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function () {
				return;
			});
	}

	getDeviceDataAllDataTypes(enabled?: boolean, namespace?: string): Promise<Model.SupportedDeviceDataType[]> {
		const queryParameters: Record<string, string> = {};

		if (enabled !== undefined) {
			queryParameters['enabled'] = enabled.toString();
		}

		if (namespace) {
			queryParameters['namespace'] = namespace;
		}

		const queryString = new URLSearchParams(queryParameters).toString();
		const endpoint = `devicedata/allDataTypes?${queryString}`;

		return this.connect()
			.then(() => this.makeRequest(endpoint, 'GET', null))
			.then(MyDataHelps.validateResponse)
			.then(response => response.json());
		}

	// Device Data V2

	queryDeviceDataV2(queryParameters: Model.DeviceDataV2Query): Promise<Model.DeviceDataV2Page> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("observedBefore")) {
			queryParameters.observedBefore = MyDataHelps.convertDateToIsoString(queryParameters.observedBefore);
		}
		if (queryParameters.hasOwnProperty("observedAfter")) {
			queryParameters.observedAfter = MyDataHelps.convertDateToIsoString(queryParameters.observedAfter);
		}
		if (queryParameters.hasOwnProperty("insertedBefore")) {
			queryParameters.insertedBefore = MyDataHelps.convertDateToIsoString(queryParameters.insertedBefore);
		}
		if (queryParameters.hasOwnProperty("insertedAfter")) {
			queryParameters.insertedAfter = MyDataHelps.convertDateToIsoString(queryParameters.insertedAfter);
		}
		if (queryParameters.hasOwnProperty("modifiedBefore")) {
			queryParameters.modifiedBefore = MyDataHelps.convertDateToIsoString(queryParameters.modifiedBefore);
		}
		if (queryParameters.hasOwnProperty("modifiedAfter")) {
			queryParameters.modifiedAfter = MyDataHelps.convertDateToIsoString(queryParameters.modifiedAfter);
		}
		MyDataHelps.flattenPropertyShallow(queryParameters, "dataSource");
		MyDataHelps.flattenPropertyShallow(queryParameters, "properties");

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'devicedata?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null, 'v2');
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	queryDeviceDataV2Aggregate(queryParameters: Model.DeviceDataV2AggregateQuery): Promise<Model.DeviceDataV2AggregatePage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("aggregateFunctions")) {
			queryParameters.aggregateFunctions = MyDataHelps.reduceArrayAndEscape(queryParameters.aggregateFunctions);
		}
		if (queryParameters.hasOwnProperty("observedBefore")) {
			queryParameters.observedBefore = MyDataHelps.convertDateToIsoString(queryParameters.observedBefore);
		}
		if (queryParameters.hasOwnProperty("observedAfter")) {
			queryParameters.observedAfter = MyDataHelps.convertDateToIsoString(queryParameters.observedAfter);
		}
		if (queryParameters.hasOwnProperty("insertedBefore")) {
			queryParameters.insertedBefore = MyDataHelps.convertDateToIsoString(queryParameters.insertedBefore);
		}
		if (queryParameters.hasOwnProperty("insertedAfter")) {
			queryParameters.insertedAfter = MyDataHelps.convertDateToIsoString(queryParameters.insertedAfter);
		}
		if (queryParameters.hasOwnProperty("modifiedBefore")) {
			queryParameters.modifiedBefore = MyDataHelps.convertDateToIsoString(queryParameters.modifiedBefore);
		}
		if (queryParameters.hasOwnProperty("modifiedAfter")) {
			queryParameters.modifiedAfter = MyDataHelps.convertDateToIsoString(queryParameters.modifiedAfter);
		}
		MyDataHelps.flattenPropertyShallow(queryParameters, "dataSource");
		MyDataHelps.flattenPropertyShallow(queryParameters, "properties");

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'devicedata/aggregate?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null, 'v2');
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	queryDeviceDataV2DailySleep(queryParameters: Model.DeviceDataV2AggregateQuery): Promise<Model.DeviceDataV2DailySleepPage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("observedBefore")) {
			queryParameters.observedBefore = MyDataHelps.convertDateToIsoString(queryParameters.observedBefore);
		}
		if (queryParameters.hasOwnProperty("observedAfter")) {
			queryParameters.observedAfter = MyDataHelps.convertDateToIsoString(queryParameters.observedAfter);
		}
		if (queryParameters.hasOwnProperty("insertedBefore")) {
			queryParameters.insertedBefore = MyDataHelps.convertDateToIsoString(queryParameters.insertedBefore);
		}
		if (queryParameters.hasOwnProperty("insertedAfter")) {
			queryParameters.insertedAfter = MyDataHelps.convertDateToIsoString(queryParameters.insertedAfter);
		}
		if (queryParameters.hasOwnProperty("modifiedBefore")) {
			queryParameters.modifiedBefore = MyDataHelps.convertDateToIsoString(queryParameters.modifiedBefore);
		}
		if (queryParameters.hasOwnProperty("modifiedAfter")) {
			queryParameters.modifiedAfter = MyDataHelps.convertDateToIsoString(queryParameters.modifiedAfter);
		}
		MyDataHelps.flattenPropertyShallow(queryParameters, "dataSource");
		MyDataHelps.flattenPropertyShallow(queryParameters, "properties");

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'devicedata/aggregate/dailysleep?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null, 'v2');
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	getDeviceDataV2AllDataTypes(enabled?: boolean, namespace?: string): Promise<Model.SupportedDeviceDataV2DataType[]> {
		const queryParameters: Record<string, string> = {};

		if (enabled !== undefined) {
			queryParameters['enabled'] = enabled.toString();
		}

		if (namespace) {
			queryParameters['namespace'] = namespace;
		}

		const queryString = new URLSearchParams(queryParameters).toString();
		const endpoint = `devicedata/allDataTypes?${queryString}`;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null, 'v2');
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	// External Accounts

	getExternalAccountProviders(search: string | null, category: string | null, pageSize: number, pageNumber: number): Promise<Model.ExternalAccountProvidersPage> {
		let searchParameters: any = {};
		if (!!search) searchParameters.search = search;
		if (!!category) searchParameters.category = category;
		if (!!pageSize) searchParameters.pageSize = pageSize;
		if (!!pageNumber) searchParameters.pageNumber = pageNumber;

		const queryString = new URLSearchParams(searchParameters).toString();
		const endpoint = 'externalaccountproviders?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	connectExternalAccount(externalAccountProviderID: number, options?: Model.ConnectExternalAccountOptions): Promise<void> {
		return new Promise<void>((resolve, reject) => {

			const messageID = mdh.nextMessageID();
			mdh.messageHandlers[messageID] = function (connectProviderAccountResponse) {
				if (connectProviderAccountResponse && connectProviderAccountResponse.success) {
					resolve();
				} else {
					reject(connectProviderAccountResponse.errorMessage)
				}
			};

			if (this.standaloneMode) {
				let standaloneModeFinalRedirectPath = options?.standaloneModeFinalRedirectPath || window.location.href;
				const mdh = this;

				// DEV: Safari blocks popups in AJAX responses. Work-around is to open a blank window before the request and set the URL later
				let providerAuthWindow;
				if (options?.openNewWindow) {
					standaloneModeFinalRedirectPath = `${standaloneModeFinalRedirectPath.split('?')[0]}?connectExternalAccount=true&messageID=${messageID}&success=%SUCCESSVALUE%`;
					let windowFeatures = null;
					if (options?.width && options?.height) {
						const w = options?.width;
						const h = options?.height;
						const y = window.outerHeight / 2 + window.screenY - (h / 2);
						const x = window.outerWidth / 2 + window.screenX - (w / 2);
						windowFeatures = "width=" + w + ",height=" + h + ",top=" + y + ",left=" + x;
					}
					providerAuthWindow = window.open(`${this.baseUrl}home/loading`, 'providerauth', windowFeatures);
				}

				const searchParameters = { finalRedirectPath: standaloneModeFinalRedirectPath };
				const queryString = new URLSearchParams(searchParameters).toString();
				const endpoint = "externalaccountproviders/" + externalAccountProviderID + "/connect?" + queryString;

				this
					.connect()
					.then(function () {
						return mdh.makeRequest(endpoint, 'POST', null);
					})
					.then(function (response) {
						return MyDataHelps.validateResponse(response);
					})
					.then(function (response) {
						return response.text();
					})
					.then(function (redirectUrl) {
						if (providerAuthWindow) {
							providerAuthWindow.location = redirectUrl.replace(/['"]+/g, '');
						} else {
							window.top.location = redirectUrl.replace(/['"]+/g, '');
						}
					})
					.catch(function (error) {
						reject(error);
					});
			} else if (window.webkit.messageHandlers.ConnectExternalAccount) {
				window.webkit.messageHandlers.ConnectExternalAccount.postMessage({
					externalAccountProviderID: externalAccountProviderID,
					messageID: messageID,
					...options
				});
			}
		});
	}

	getExternalAccounts(): Promise<Model.ExternalAccount[]> {
		const endpoint = 'externalaccounts';
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	refreshExternalAccount(accountID: number): Promise<void> {
		const endpoint = 'externalaccounts/refresh/' + accountID;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'POST', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function () {
				return;
			});
	}

	deleteExternalAccount(accountID: number): Promise<any> {
		const endpoint = 'externalaccounts/delete/' + encodeURIComponent(accountID);
		const mdh = this;

		if (this.isStandaloneMode()) {
			return this
				.connect()
				.then(function () {
					return mdh.makeRequest(endpoint, 'DELETE', null);
				})
				.then(function (response) {
					return MyDataHelps.validateResponse(response);
				});
		}

		return new Promise(function (resolve, reject) {
			if (window.webkit.messageHandlers.DeleteProviderAccount) {
				const messageID = mdh.nextMessageID();
				mdh.messageHandlers[messageID] = function (deleteProviderAccountResponse) {
					if (!deleteProviderAccountResponse.success) {
						reject(deleteProviderAccountResponse)
					} else {
						resolve(deleteProviderAccountResponse);
					}
				};
				window.webkit.messageHandlers.DeleteProviderAccount.postMessage({
					messageID: messageID,
					accountID: accountID
				});
			} else {
				reject();
			}
		});
	}

	getFeaturedProviders(context?: string): Promise<Model.ExternalAccountProvider[]> {
		const queryParameters: Record<string, string> = {};

		if (context) {
			queryParameters['featuredProvidersContext'] = context;
		}

		const queryString = new URLSearchParams(queryParameters).toString();
		const endpoint = `featuredexternalaccountproviders?${queryString}`;

		return this.connect()
			.then(() => this.makeRequest(endpoint, 'GET', null))
			.then(MyDataHelps.validateResponse)
			.then(response => response.json());
	}

	// Notifications

	queryNotifications(queryParameters: Model.NotificationQueryParameters): Promise<Model.NotificationsPage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("sentBefore")) {
			queryParameters.sentBefore = MyDataHelps.convertDateToIsoString(queryParameters.sentBefore);
		}
		if (queryParameters.hasOwnProperty("sentAfter")) {
			queryParameters.sentAfter = MyDataHelps.convertDateToIsoString(queryParameters.sentAfter);
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'notifications?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	// Fitbit

	queryFitbitDailySummaries(queryParameters: Model.FitbitQuery): Promise<Model.FitbitDailySummariesPage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("startDate")) {
			queryParameters.startDate = MyDataHelps.convertDateToIsoString(queryParameters.startDate);
		}
		if (queryParameters.hasOwnProperty("endDate")) {
			queryParameters.endDate = MyDataHelps.convertDateToIsoString(queryParameters.endDate);
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'fitbit/dailySummaries?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	queryFitbitSleepLogs(queryParameters: Model.FitbitQuery): Promise<Model.FitbitSleepLogsPage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("startDate")) {
			queryParameters.startDate = MyDataHelps.convertDateToIsoString(queryParameters.startDate);
		}
		if (queryParameters.hasOwnProperty("endDate")) {
			queryParameters.endDate = MyDataHelps.convertDateToIsoString(queryParameters.endDate);
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'fitbit/sleepLogs?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	// AppleHealth

	queryAppleHealthActivitySummaries(queryParameters: Model.AppleHealthQuery): Promise<Model.AppleHealthActivitySummaryPage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("startDate")) {
			queryParameters.startDate = MyDataHelps.convertDateToIsoString(queryParameters.startDate);
		}
		if (queryParameters.hasOwnProperty("endDate")) {
			queryParameters.endDate = MyDataHelps.convertDateToIsoString(queryParameters.endDate);
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'appleHealth/activitySummaries?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	queryAppleHealthWorkouts(queryParameters: Model.AppleHealthQuery): Promise<Model.AppleHealthWorkoutPage> {
		queryParameters = Object.assign({}, queryParameters);

		if (queryParameters.hasOwnProperty("startDate")) {
			queryParameters.startDate = MyDataHelps.convertDateToIsoString(queryParameters.startDate);
		}
		if (queryParameters.hasOwnProperty("endDate")) {
			queryParameters.endDate = MyDataHelps.convertDateToIsoString(queryParameters.endDate);
		}

		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'appleHealth/workouts?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	// Files

	uploadFile(file: File, category: string, fileName?: string): Promise<void> {

		if (file.size > 200 * 1024 * 1024) {
			throw new Error("File size exceeds the maximum allowed size of 200MB.");
		}

		const mdh = this;
		const endpoint = 'files?category=' + category + '&fileName=' + encodeURIComponent(fileName ?? file.name);

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'POST', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			})
			.then(function (json) {
				return fetch(json.preSignedUrl, {
					method: 'PUT',
					body: file,
					headers: {
						'Content-Type': file.type,
						'x-amz-server-side-encryption': 'AES256'
					}
				});
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function () {
				return;
			});
	}

	queryFiles(queryParameters: Model.UploadedFileQuery): Promise<Model.UploadedFilesPage> {
		const queryString = new URLSearchParams(queryParameters as Record<string, any>).toString();
		const endpoint = 'files?' + queryString;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	getFileDownloadUrl(key: string): Promise<Model.DownloadedFile> {

		if (!key) throw new Error("Please specify the key of the file to download.");

		const endpoint = 'files/download?key=' + key;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	deleteFile(key: string): Promise<void> {

		if (!key) throw new Error("Please specify the key of the file to delete.");

		const endpoint = 'files?key=' + key;
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'DELETE', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function () {
				return;
			});
	}

	// Authorization

	connect(): Promise<void> {
		const mdh = this;
		let refreshDelegatedAccessToken = function () {
			mdh.token = null;

			mdh.refreshTokenPromise = new Promise(function (resolve, reject) {
				let messageID = mdh.nextMessageID();
				mdh.messageHandlers[messageID] = function (tokenResponse) {
					if (tokenResponse.success) {
						mdh.tokenExpires = Date.now() + (tokenResponse.data.expires_in * 1000);
						mdh.token = tokenResponse.data;
						mdh.baseUrl = tokenResponse.baseUrl;
						setTimeout(refreshDelegatedAccessToken, ((tokenResponse.data.expires_in - mdh.accessTokenRenewalBufferSeconds) * 1000));
						resolve(null);
					} else {
						mdh.token = null;
						reject(tokenResponse.message);
					}
					mdh.messageHandlers[messageID] = null;
					mdh.refreshTokenPromise = null;
				};

				window.webkit.messageHandlers.GetDelegatedAccessToken.postMessage({ messageID: messageID });
			});
		};

		if (this.token && Date.now() < this.tokenExpires) {
			return Promise.resolve();
		}

		if (this.isStandaloneMode() && !this.isEmbeddedMode()) {
			let error = this.token ? "access token is expired or invalid" : "access token must be explicitly provided using the setParticipantAccess method";
			console.log(error);
			return Promise.reject(error);
		}

		if (!this.refreshTokenPromise) {
			refreshDelegatedAccessToken();
		}

		// Re-use promise if refresh already in progress
		return this.refreshTokenPromise;
	}

	setParticipantAccessToken(token: Model.Token, baseUrl?: string): void {
		this.enableStandaloneMode();
		this.token = token;
		if (baseUrl) {
			this.baseUrl = baseUrl;
		}
		this.tokenExpires = Date.now() + (this.token.expires_in * 1000);
		const mdh = this;
		setTimeout(function () {
			mdh.triggerEvent({ type: "tokenWillExpire" });
		}, ((this.token.expires_in - this.accessTokenRenewalBufferSeconds) * 1000));
	}

	// Miscellaneous

	isStandaloneMode() {
		return this.standaloneMode;
	}

	isEmbeddedMode() {
		return this.embeddedMode;
	}

	enableStandaloneMode(embeddedMode: boolean = false, baseUrl?: string) {
		this.standaloneMode = true;
		this.embeddedMode = embeddedMode;
		if (baseUrl) {
			this.baseUrl = baseUrl;
		}
		if (window.webkit) {
			window.webkit.messageHandlers = embeddedMode ? {
				GetDelegatedAccessToken: window.webkit.messageHandlers.GetDelegatedAccessToken,
				OpenExternalLink: window.webkit.messageHandlers.OpenExternalLink,
				OpenEmbeddedLink: window.webkit.messageHandlers.OpenEmbeddedLink,
				OpenExternalApplication: window.webkit.messageHandlers.OpenExternalApplication,
				PopNavigation: window.webkit.messageHandlers.PopNavigation,
				Dismiss: window.webkit.messageHandlers.Dismiss,
			} : {};
		}
	}

	setStatusBarStyle(style: Model.StatusBarStyle): void {
		if (window.webkit.messageHandlers.SetStatusBarStyle) {
			window.webkit.messageHandlers.SetStatusBarStyle.postMessage({ style: style });
		}
	}

	getDeviceInfo(): Promise<Model.DeviceInfo> {
		let mdh = this;
		return new Promise<Model.DeviceInfo>(function (resolve, reject) {
			if (mdh.isStandaloneMode()) {
				resolve(null);
			} else if (window.webkit.messageHandlers.GetDeviceInfo) {
				let messageID = mdh.nextMessageID();
				mdh.messageHandlers[messageID] = function (deviceInfoResponse) {
					resolve(deviceInfoResponse.data);
				};
				window.webkit.messageHandlers.GetDeviceInfo.postMessage({ messageID: messageID });
			} else {
				reject();
			}
		});
	}

	setSupportedLanguages(languages: string[]) {
		this.supportedLanguages.length = 0;
		for (let i = 0; i < languages.length; i++) {
			this.supportedLanguages.push(languages[i].replace("_", "-"));
		}
	}

	getStepConfiguration(): Promise<Model.StepConfiguration> {
		let mdh = this;
		return new Promise<Model.StepConfiguration>(function (resolve, reject) {
			if (mdh.isStandaloneMode()) {
				resolve(null);
			} else if (window.webkit.messageHandlers.GetStepConfiguration) {
				let messageID = mdh.nextMessageID();
				mdh.messageHandlers[messageID] = function (stepConfigurationResponse) {
					resolve(stepConfigurationResponse.data);
				};
				window.webkit.messageHandlers.GetStepConfiguration.postMessage({ messageID: messageID });
			} else {
				reject();
			}
		});
	}

	getSurveyContext(): Promise<Model.SurveyContext> {
		let mdh = this;
		return new Promise(function (resolve, reject) {
			if (mdh.isStandaloneMode()) {
				resolve(null);
			}
			mdh.getDeviceInfo().then(function (deviceInfo) {
				if (deviceInfo.platform !== "Web") {
					resolve({ surveyMode: "Survey" });
				}
				else if (window.webkit.messageHandlers.GetSurveyContext) {
					let messageID = mdh.nextMessageID();
					mdh.messageHandlers[messageID] = function (surveyContextResponse) {
						resolve(surveyContextResponse.data);
					};
					window.webkit.messageHandlers.GetSurveyContext.postMessage({ messageID: messageID });
				}
				else {
					reject();
				}
			}).catch(function (error) {
				reject();
			});
		});
	}

	getCurrentSurveyAnswers(): Promise<Model.CurrentSurveyAnswer[]> {
		let mdh = this;
		return new Promise<Model.CurrentSurveyAnswer[]>(function (resolve, reject) {
			if (mdh.isStandaloneMode()) {
				resolve(null);
			} else if (window.webkit.messageHandlers.GetCurrentSurveyAnswers) {
				let messageID = mdh.nextMessageID();
				mdh.messageHandlers[messageID] = function (currentSurveyAnswersResponse) {
					resolve(currentSurveyAnswersResponse.data);
				};
				window.webkit.messageHandlers.GetCurrentSurveyAnswers.postMessage({ messageID: messageID });
			} else {
				reject();
			}
		});
	}

	setCurrentLanguage(language: string) {
		if (language) {
			let supportedLanguage = this.getSupportedLanguage(language);
			if (supportedLanguage) {
				this.language = supportedLanguage;
			} else {
				console.warn(`language ${language} not supported`);
			}
		} else {
			this.language = language;
		}
	}

	getCurrentLanguage(): string {
		let searchParams = new URLSearchParams(window.location.search);
		let lang;
		if (searchParams.has("lang")) {
			lang = this.getSupportedLanguage(searchParams.get("lang"));
			if (lang) {
				return lang;
			} else {
				console.warn(`language ${searchParams.get("lang")} not supported`);
			}
		}
		return this.language || navigator.language;
	}

	showGoogleFitSettings(): void {
		if (window.webkit.messageHandlers.ShowGoogleFitSettings) {
			window.webkit.messageHandlers.ShowGoogleFitSettings.postMessage({});
		}
	}

	showHealthConnectSettings(): void {
		if (window.webkit.messageHandlers.HealthConnectSettings) {
			window.webkit.messageHandlers.HealthConnectSettings.postMessage({});
		}
	}

	showHealthConnectPrompt(): void {
		if (window.webkit.messageHandlers.HealthConnectPrompt) {
			window.webkit.messageHandlers.HealthConnectPrompt.postMessage({});
		}
	}

	getHealthConnectStatus(): Promise<Model.HealthConnectStatus> {
		let mdh = this;
		return new Promise<Model.HealthConnectStatus>(function (resolve, reject) {
			if (mdh.isStandaloneMode()) {
				resolve(null);
			} else if (window.webkit.messageHandlers.HealthConnectStatus) {
				let messageID = mdh.nextMessageID();
				mdh.messageHandlers[messageID] = function (statusResponse) {
					resolve(statusResponse.data);
				};
				window.webkit.messageHandlers.HealthConnectStatus.postMessage({ messageID: messageID });
			} else {
				reject();
			}
		});
	}

	showHealthConnectPhrPrompt(): void {
		if (window.webkit.messageHandlers.HealthConnectPhrPrompt) {
			window.webkit.messageHandlers.HealthConnectPhrPrompt.postMessage({});
		}
	}

	getHealthConnectPhrStatus(): Promise<Model.HealthConnectPhrStatus> {
		let mdh = this;
		return new Promise<Model.HealthConnectPhrStatus>(function (resolve, reject) {
			if (mdh.isStandaloneMode()) {
				resolve(null);
			} else if (window.webkit.messageHandlers.HealthConnectPhrStatus) {
				let messageID = mdh.nextMessageID();
				mdh.messageHandlers[messageID] = function (statusResponse) {
					resolve(statusResponse.data);
				};
				window.webkit.messageHandlers.HealthConnectPhrStatus.postMessage({ messageID: messageID });
			} else {
				reject();
			}
		});
	}

	requestReview(cooldownDays?: number): void {
		if (window.webkit.messageHandlers.RequestReview) {
			window.webkit.messageHandlers.RequestReview.postMessage({ cooldownDays: cooldownDays });
		}
	}

	trackCustomEvent(event: Model.CustomEventInfo): Promise<void> {
		const endpoint = 'customevents';
		const mdh = this;

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'POST', event);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function () {
				return;
			});
	}

	getDataCollectionSettings(): Promise<Model.DataCollectionSettings> {
		const mdh = this;
		const endpoint = 'datacollectionsettings';

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	getDataAvailability(): Promise<Model.DataAvailability> {
		const mdh = this;
		const endpoint = 'dataavailability';

		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, 'GET', null);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return response.json();
			});
	}

	invokeCustomApi(customApi: string, method: string, queryParameters: string | string[][] | Record<string, string> | URLSearchParams | null | undefined, jsonResponse: boolean): Promise<any> {
		if (customApi.includes('?')) {
			throw new Error("Cannot include query parameters directly in the \"customApi\" string. Provide as an argument for \"queryParameters\" instead.");
		}

		let endpoint = 'custom/' + customApi;
		method = method.toUpperCase();
		if (queryParameters !== null && queryParameters !== undefined && (method === 'GET' || method === 'DELETE')) {
			let parameterString = new URLSearchParams(queryParameters).toString();
			endpoint += "?" + parameterString;
			queryParameters = null;
		}

		const mdh = this;
		return this
			.connect()
			.then(function () {
				return mdh.makeRequest(endpoint, method, queryParameters);
			})
			.then(function (response) {
				return MyDataHelps.validateResponse(response);
			})
			.then(function (response) {
				return jsonResponse ? response.json() : undefined;
			});
	}

	constructor() {
		if (this.handleConnectExternalAccountResponse()) {
			return;
		}

		let mdh = this;
		let applicationHost = getParentOrigin();
		let supportedActions;

		function getParentOrigin(): string {
			let ancestorOrigins = document.location.ancestorOrigins;
			if (ancestorOrigins && ancestorOrigins[0]) {
				return ancestorOrigins[0];
			}

			return '*';
		}

		function acceptableOrigin(origin) {
			if (origin === '*') return true;

			let sourceURL = new URL(origin);
			for (let k = 0; k < mdh.acceptableParentDomains.length; k++) {
				if (sourceURL.hostname === mdh.acceptableParentDomains[k] || sourceURL.hostname.endsWith("." + mdh.acceptableParentDomains[k])) {
					return true;
				}
			}
			return false;
		}

		function validateWindowMessageOrigin(message) {
			if (mdh.isStandaloneMode() && message.origin === window.location.origin) {
				return true;
			}

			if (applicationHost === "*") {
				return acceptableOrigin(message.origin);
			}

			return message.origin === applicationHost;
		}

		function addActions(actions) {
			for (let action in actions) {
				if (!window.webkit.messageHandlers[action]) {
					try {
						window.webkit.messageHandlers[action] = { postMessage: actions[action] };
					} catch (error) {
						console.log("Unable to add missing actions on this platform: " + error);
						return;
					}
				}
			}
		}

		function receiveWindowMessage(message) {
			if (!validateWindowMessageOrigin(message)) {
				// todo: this can generate tons of console errors when hosting some apps within webappstep (youtube content, for example )
				console.error("message.origin '" + message.origin + "' is not allowed.");
				throw "message.origin '" + message.origin + "' is not allowed.";
			}

			if (message.data.messageID) {
				mdh.setActionResult(message.data);
			} else {
				mdh.triggerEvent(message.data);
			}
		}

		function windowHasAnyActions() {
			for (let action in supportedActions) {
				if (window.webkit.messageHandlers[action]) {
					return true;
				}
			}
			for (let action in mdh.unsupportedActions) {
				if (window.webkit.messageHandlers[mdh.unsupportedActions[action]]) {
					return true;
				}
			}
			return false;
		}

		if (!acceptableOrigin(applicationHost)) {
			console.log("Detected unsupported parent origin domain.");
		}

		if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.ResearchKit) {
			// Presence of ResearchKit handler indicates the SDK is being used from within a WebViewStep. For pre-SDK backwards compatiblity,
			// WebViewStep setup registers the following handlers: GetDeviceInfo, DeleteProviderAccount, ConnectExternalAccount
			// ShowParticipantWebVisualizationPDF, ResearchKit
			supportedActions = {
				"GetDelegatedAccessToken": function (data) {
					window.parent.postMessage({
						name: 'GetDelegatedAccessToken',
						messageID: data.messageID
					}, applicationHost)
				},
				"OpenExternalLink": function (data) {
					window.parent.postMessage({
						name: 'OpenExternalUrl',
						url: data
					}, applicationHost)
				},
				"OpenEmbeddedLink": function (data) {
					window.parent.postMessage({
						name: 'OpenEmbeddedUrl',
						url: data
					}, applicationHost)
				},
				"GetCurrentSurveyAnswers": function (data) {
					window.parent.postMessage({
						name: 'GetCurrentSurveyAnswers',
						messageID: data.messageID
					}, applicationHost)
				},
				"GetStepConfiguration": function (data) {
					window.parent.postMessage({
						name: 'GetStepConfiguration',
						messageID: data.messageID
					}, applicationHost)
				},
				"GetSurveyContext": function (data) {
					window.parent.postMessage({
						name: 'GetSurveyContext',
						messageID: data.messageID
					}, applicationHost);
				},
				"CompleteStep": function (data) {
					window.parent.postMessage({
						name: 'CompleteStep',
						messageID: data.messageID,
						answer: data.answer
					}, applicationHost)
				}
			};
		} else {
			supportedActions = {
				"GetDelegatedAccessToken": function (data) {
					window.parent.postMessage({
						name: 'GetDelegatedAccessToken',
						messageID: data.messageID
					}, applicationHost)
				},
				"GetDeviceInfo": function (data) {
					window.parent.postMessage({ name: 'GetDeviceInfo', messageID: data.messageID }, applicationHost)
				},
				"StartParticipantSurvey": function (data) {
					window.parent.postMessage({
						name: 'StartParticipantSurvey',
						messageID: data.messageID,
						surveyName: data.surveyName
					}, applicationHost)
				},
				"OpenExternalLink": function (data) {
					window.parent.postMessage({ name: 'OpenExternalUrl', url: data }, applicationHost)
				},
				"OpenEmbeddedLink": function (data) {
					window.parent.postMessage({ name: 'OpenEmbeddedUrl', url: data }, applicationHost)
				},
				"OpenExternalApplication": function (data) {
					window.parent.postMessage({
						name: 'OpenApplication',
						messageID: data.messageID,
						url: data.url,
						modal: data.modal
					}, applicationHost)
				},
				"Dismiss": function () {
					window.parent.postMessage({ name: 'Dismiss' }, applicationHost)
				},
				"PopNavigation": function () {
					window.parent.postMessage({ name: 'Back' }, applicationHost)
				},
				"ShowTab": function (data) {
					window.parent.postMessage({ name: 'ShowTab', tabKey: data }, applicationHost)
				},
				"ShowParticipantDashboard": function (data) {
					window.parent.postMessage({
						name: 'ShowParticipantDashboard',
						dashboardKey: data.dashboardKey,
						modal: data.modal
					}, applicationHost)
				},
				"ShowParticipantWebVisualization": function (data) {
					window.parent.postMessage({
						name: 'ShowParticipantWebVisualization',
						visualizationKey: data.visualizationKey,
						parameters: data.parameters,
						modal: data.modal
					}, applicationHost)
				},
				"ShowParticipantWebVisualizationPDF": function (data) {
					window.parent.postMessage({
						name: 'ShowParticipantWebVisualizationPDF',
						visualizationKey: data.visualizationKey,
						parameters: data.parameters,
						modal: data.modal,
						landscape: data.landscape,
						htmlViewerZoom: data.htmlViewerZoom
					}, applicationHost)
				},
				"DeleteProviderAccount": function (data) {
					window.parent.postMessage({
						name: 'DeleteProviderAccount',
						messageID: data.messageID,
						accountID: data.accountID
					}, applicationHost)
				},
				"ConnectExternalAccount": function (data) {
					window.parent.postMessage({
						name: 'ConnectExternalAccount',
						externalAccountProviderID: data.externalAccountProviderID,
						messageID: data.messageID,
						openNewWindow: data.openNewWindow,
						width: data.width,
						height: data.height
					}, applicationHost)
				},
				"ShowProject": function (data) {
					window.parent.postMessage({ name: 'ShowProject', code: data.code }, applicationHost)
				},
				"JoinProject": function (data) {
					window.parent.postMessage({ name: 'JoinProject', code: data.code }, applicationHost)
				},
				"GetCurrentSurveyAnswers": function (data) {
					window.parent.postMessage({
						name: 'GetCurrentSurveyAnswers',
						messageID: data.messageID
					}, applicationHost)
				},
				"GetStepConfiguration": function (data) {
					window.parent.postMessage({
						name: 'GetStepConfiguration',
						messageID: data.messageID
					}, applicationHost)
				},
				"CompleteStep": function (data) {
					window.parent.postMessage({
						name: 'CompleteStep',
						messageID: data.messageID,
						answer: data.answer
					}, applicationHost)
				}
			};
		}

		if (!window.webkit || !window.webkit.messageHandlers || !windowHasAnyActions()) {
			window.webkit = {
				messageHandlers: {}
			};
		}

		addActions(supportedActions);

		window.addEventListener("message", receiveWindowMessage, false);

		if (this.hasEmbeddedModeUrlParameter()) {
			mdh.enableStandaloneMode(true);
		}
	}

	private readonly acceptableParentDomains = [
		'localhost',
		'careevolution.com',
		'internal',
		'b3-deploys.com',
		'mydatahelps.org',
		'platform.joinallofus.org',
		'careevolution.dev',
		'ce.dev',
		'mydatahelps.dev'
	];

	private readonly accessTokenRenewalBufferSeconds = 120;
	private readonly unsupportedActions = ["SetHeight", "ResearchKit"];
	private readonly supportedEvents = ["surveyDidFinish", "applicationDidBecomeVisible", "externalAccountSyncComplete", "tokenWillExpire", "healthConnectSyncComplete", "healthConnectPhrSyncComplete"];
	private readonly supportedLanguages = [];

	private currentMessageID = 1;
	private messageHandlers = [];
	private registeredEventHandlers = {};
	private refreshTokenPromise = null;
	private standaloneMode = false;
	private embeddedMode = false;

	private nextMessageID() {
		return this.currentMessageID++;
	}

	private makeUrl(endpoint, apiVersion) {
		if (!this.baseUrl) {
			console.error("Cannot use makeUrl without MyDataHelps.baseUrl.");
			throw "Cannot use makeUrl without MyDataHelps.baseUrl.";
		}

		return this.baseUrl + 'api/' + apiVersion + '/delegated/' + endpoint;
	}

	private makeRequest(endpoint, method, body, apiVersion = "v1") {
		if (!this.token || !this.token.access_token) {
			throw "No access_token available for request authorization.";
		}

		let url = this.makeUrl(endpoint, apiVersion);

		let headers = new Headers();
		headers.append('Authorization', 'Bearer ' + this.token.access_token);
		headers.append('Accept', 'application/json, text/javascript, */*; q=0.01');
		headers.append('Accept-Language', this.getCurrentLanguage());
		if (!!body) {
			headers.append('Content-Type', 'application/json');
		}

		let init: any = {
			method: method,
			headers: headers
		};
		if (!!body) {
			init.body = JSON.stringify(body);
		}

		return fetch(url, init);
	}

	private getSupportedLanguage(language: string): string {
		language = language.replace("_", "-");
		if (this.supportedLanguages.length === 0) {
			return language;
		} else {
			if (this.supportedLanguages.find((l) => l === language)) {
				return language;
			} else if (language.indexOf("-") > 0) {
				language = language.substring(0, language.indexOf("-"));
				if (this.supportedLanguages.find((l) => l === language)) {
					return language;
				}
			}
		}
	}

	private static validateResponse(response) {
		if (!response.ok) {
			throw response.statusText;
		}

		return response;
	}

	private static escapeParam(param) {
		return String(param)
			.replaceAll("\\", "\\\\")
			.replaceAll(",", "\\,");
	}

	private static reduceArrayAndEscape(param) {
		if (Array.isArray(param)) {
			let escapedParams = param.map(function (elem) {
				return MyDataHelps.escapeParam(elem);
			});
			return escapedParams.join(",");
		}

		return this.escapeParam(param);
	}

	private static convertDateToIsoString(date) {
		let paramAsDate = new Date(date);
		if (isNaN(paramAsDate.getTime())) {
			throw "Cannot interpret parameter as Date";
		}

		return paramAsDate.toISOString();
	}

	private static flattenPropertyShallow(params, property) {
		if (params[property] === undefined || params[property] === null) return;

		for (var key in params[property]) {
			if (params[property].hasOwnProperty(key)) {
				params[`${property}.${key}`] = MyDataHelps.escapeParam(params[property][key]);
			}
		}

		delete params[property];
	}

	private startEmbeddedSurveyInternal(surveyUrl) {
		let mdhSurveyModalId = 'mydatahelps-survey-modal';
		let mdh = this;

		return new Promise(function (resolve, reject) {
			if (document.getElementById(mdhSurveyModalId)) {
				reject("Survey already in progress");
				return;
			}

			window.addEventListener("message", function listener(message) {
				if (message.origin !== new URL(mdh.baseUrl).origin) {
					return;
				}
				let frame = (document.getElementById('mydatahelps-survey-frame') as HTMLFrameElement).contentWindow;
				if (message.source !== frame) {
					return;
				}
				if (message.data.name === 'SurveyWindowInitialized') {
					document.getElementById(mdhSurveyModalId).className += " loaded";
				} else if (message.data.name === 'SurveyFinished') {
					document.getElementById(mdhSurveyModalId).remove();
					document.body.className = document.body.className.replace("no-scroll", "");
					window.removeEventListener("message", listener, true);
					resolve(message.data);
				}
			}, true);

			let surveyModal = document.createElement('div');
			surveyModal.className = 'mydatahelps-survey-modal';
			surveyModal.id = mdhSurveyModalId;
			surveyModal.innerHTML = "<div class='mydatahelps-survey'><div class='loader'>Loading...</div><iframe id='mydatahelps-survey-frame' allow='camera' sandbox='allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts' src='" + surveyUrl + "'></iframe></div>";
			document.body.append(surveyModal);
			document.body.className += ' no-scroll';
		});
	}

	private startDelegatedSurvey(surveyUrl) {
		let mdhSurveyModalId = 'mydatahelps-survey-modal';
		let mdh = this;

		if (document.getElementById(mdhSurveyModalId)) {
			return;
		}

		window.addEventListener("message", function listener(message) {
			if (message.origin !== new URL(mdh.baseUrl).origin) {
				return;
			}
			let frame = (document.getElementById('mydatahelps-survey-frame') as HTMLFrameElement).contentWindow;
			if (message.source !== frame) {
				return;
			}
			if (message.data.name === 'GetDelegatedAccessToken') {
				mdh.getParticipantInfo().then((participant) => {
					frame.postMessage({ name: "DelegatedAccessTokenResponse", accessToken: mdh.token, baseUrl: mdh.baseUrl, participantID: participant.participantID }, '*');
				});
			}
			else if (message.data.name === 'SurveyWindowInitialized') {
				document.getElementById(mdhSurveyModalId).className += " loaded";
			}
			else if (message.data.name === 'SurveyFinished') {
				document.getElementById(mdhSurveyModalId).remove();
				document.body.className = document.body.className.replace("no-scroll", "");
				window.removeEventListener("message", listener, true);
				message.data.type = 'surveyDidFinish';
				mdh.triggerEvent(message.data);
			}
		}, true);

		let surveyModal = document.createElement('div');
		surveyModal.className = 'mydatahelps-survey-modal';
		surveyModal.id = mdhSurveyModalId;
		surveyModal.innerHTML = `<div class='mydatahelps-survey'><div class='loader'>Loading...</div><iframe id='mydatahelps-survey-frame' allow='camera' sandbox='allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts' src='${surveyUrl}&lang=${mdh.getCurrentLanguage()}'></iframe></div>`;
		document.body.append(surveyModal);
		document.body.className += ' no-scroll';
	}

	private handleConnectExternalAccountResponse() {
		const params = new URLSearchParams(window.location.search);
		if (window.opener && params.get('connectExternalAccount') && params.get('messageID') && params.get('success')) {
			window.opener.postMessage({
				name: 'ConnectExternalAccountResponse',
				messageID: parseInt(params.get('messageID')),
				success: params.get('success') === 'true'
			}, window.location.origin);
			window.close();
			return true;
		}
	}

	private hasEmbeddedModeUrlParameter() {
		const embeddedModeKey = '_e';
		const params = new URLSearchParams(window.location.search);
		if (params.has(embeddedModeKey) && params.get(embeddedModeKey) !== 'false') {
			return true;
		}
	}
}

//iOS/Android directly invoke window.MyDataHelps and window.RKStudioClient
//so it's necessary to explicitly ensure they are present on the window
const mdh = new MyDataHelps();

window.MyDataHelps = mdh;

// For backwards compatibility
window.RKStudioClient = {};
window.RKStudioClient.setActionResult = function (data) {
	window.MyDataHelps.setActionResult(data);
};

export default mdh;
export * from './types';
